import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./GetTheApp.css";

// /app - "Is there an app?" (Paul, 19 Oct 2026). The site installs from the browser as a
// home-screen app: no app store, same login, same membership. This page shows the
// steps for the device it is opened on, and an Install button where the browser
// offers one (Chrome / Edge on Android and desktop).

const ua = () => (typeof navigator !== "undefined" ? navigator.userAgent || "" : "");
const isIOS = () => /iPhone|iPad|iPod/i.test(ua()) || (/Macintosh/i.test(ua()) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1);
const isAndroid = () => /Android/i.test(ua());
const isStandalone = () =>
  typeof window !== "undefined" &&
  ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true);

const ShareIcon = () => (
  <svg className="gta-ico" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3l4 4h-3v8h-2V7H8l4-4z" fill="currentColor" />
    <path d="M5 11h3v2H7v7h10v-7h-1v-2h3v11H5z" fill="currentColor" />
  </svg>
);
const DotsIcon = () => (
  <svg className="gta-ico" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="5" r="2" fill="currentColor" /><circle cx="12" cy="12" r="2" fill="currentColor" /><circle cx="12" cy="19" r="2" fill="currentColor" />
  </svg>
);
const PlusBoxIcon = () => (
  <svg className="gta-ico" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="4" y="4" width="16" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export default function GetTheApp() {
  const [device, setDevice] = useState("other");
  const [installed, setInstalled] = useState(false);
  const [promptEvt, setPromptEvt] = useState(null);
  const [justInstalled, setJustInstalled] = useState(false);

  useEffect(() => {
    setDevice(isIOS() ? "ios" : isAndroid() ? "android" : "other");
    setInstalled(isStandalone());
    const onPrompt = (e) => { e.preventDefault(); setPromptEvt(e); };
    const onInstalled = () => { setJustInstalled(true); setPromptEvt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!promptEvt) return;
    promptEvt.prompt();
    try { await promptEvt.userChoice; } catch (e) { /* user dismissed */ }
    setPromptEvt(null);
  };

  const Tabs = () => (
    <div className="gta-tabs" role="tablist" aria-label="Your device">
      {[["ios", "iPhone / iPad"], ["android", "Android"], ["other", "Computer"]].map(([k, label]) => (
        <button key={k} type="button" role="tab" aria-selected={device === k} className={"gta-tab" + (device === k ? " is-on" : "")} onClick={() => setDevice(k)}>{label}</button>
      ))}
    </div>
  );

  return (
    <div className="gta-root">
      <Helmet>
        <title>Get the app — Bridge Champions</title>
        <meta name="description" content="Add Bridge Champions to your phone's home screen. No app store needed: same login, same membership." />
        <link rel="canonical" href="https://bridgechampions.com/app" />
      </Helmet>
      <div className="gta-page">
        <div className="gta-hero">
          <img className="gta-appicon" src="/icons/icon-192.png" width="96" height="96" alt="" />
          <div>
            <h1 className="gta-h1">Bridge Champions on your home screen</h1>
            <p className="gta-lead">No app store needed. Add the site to your home screen and it opens full-screen like an app, with your usual login and membership.</p>
          </div>
        </div>

        {installed || justInstalled ? (
          <div className="gta-card gta-done">
            <h2 className="gta-h2">{justInstalled ? "Installed." : "You're using the app."}</h2>
            <p>{justInstalled ? "Look for the Bridge Champions icon on your home screen." : "This is Bridge Champions running from your home screen."}</p>
            <a className="gta-btn" href="/just-play">Just Play</a>
          </div>
        ) : (
          <>
            {promptEvt ? (
              <div className="gta-card gta-install">
                <button type="button" className="gta-btn gta-btn--big" onClick={install}>Install Bridge Champions</button>
                <p className="gta-muted gta-small">One tap. You can remove it any time like any other app.</p>
              </div>
            ) : null}

            <Tabs />

            {device === "ios" ? (
              <div className="gta-card" role="tabpanel">
                <h2 className="gta-h2">On iPhone or iPad (Safari)</h2>
                <ol className="gta-steps">
                  <li>Open <strong>bridgechampions.com</strong> in <strong>Safari</strong>. (It has to be Safari for this step, not Chrome.)</li>
                  <li>Tap the <strong>Share</strong> button <ShareIcon /> at the bottom of the screen (on iPad it is at the top).</li>
                  <li>Scroll down the list and tap <strong>Add to Home Screen</strong> <PlusBoxIcon />.</li>
                  <li>Tap <strong>Add</strong> in the top right.</li>
                </ol>
                <p className="gta-muted">The Bridge Champions icon appears on your home screen. Open it from there from now on.</p>
              </div>
            ) : null}

            {device === "android" ? (
              <div className="gta-card" role="tabpanel">
                <h2 className="gta-h2">On Android (Chrome)</h2>
                <ol className="gta-steps">
                  <li>Open <strong>bridgechampions.com</strong> in <strong>Chrome</strong>.</li>
                  <li>Tap the <strong>three dots</strong> <DotsIcon /> in the top right.</li>
                  <li>Tap <strong>Add to Home screen</strong> (on some phones it says <strong>Install app</strong>).</li>
                  <li>Tap <strong>Add</strong> or <strong>Install</strong>.</li>
                </ol>
                <p className="gta-muted">Samsung Internet: tap the menu, then <strong>Add page to</strong>, then <strong>Home screen</strong>.</p>
              </div>
            ) : null}

            {device === "other" ? (
              <div className="gta-card" role="tabpanel">
                <h2 className="gta-h2">On a computer (Chrome or Edge)</h2>
                <ol className="gta-steps">
                  <li>Open <strong>bridgechampions.com</strong> in Chrome or Edge.</li>
                  <li>Click the <strong>install icon</strong> at the right-hand end of the address bar (a small screen with a down arrow), or the browser menu then <strong>Install Bridge Champions</strong> / <strong>Apps &rarr; Install this site as an app</strong>.</li>
                  <li>Click <strong>Install</strong>.</li>
                </ol>
                <p className="gta-muted">It opens in its own window and appears in your Start menu or Dock like any other program. On a Mac, Safari can do this too: <strong>File &rarr; Add to Dock</strong>.</p>
              </div>
            ) : null}

            <div className="gta-card gta-faq">
              <h2 className="gta-h2">Good to know</h2>
              <ul className="gta-list">
                <li><strong>Same account.</strong> Log in once and it stays logged in, exactly as on the website.</li>
                <li><strong>Same membership.</strong> Nothing to buy again; everything you have on the site is there.</li>
                <li><strong>Always up to date.</strong> There is nothing to update; it is the live site.</li>
                <li><strong>Removing it</strong> is the same as any app: press and hold the icon, then remove.</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
